import { Link } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { Badge } from "@/components/ui/badge";
import { StageBadge } from '@/components/shared/StageBadge';
import { ProgressStepper } from '@/components/shared/ProgressStepper';
import { DetailGrid } from '@/components/backoffice/DetailGrid';
import { PaymentSummary } from '@/components/shared/PaymentSummary';
import { DocumentSection } from '@/components/shared/DocumentSection';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, formatDate, formatCents } from "@/lib/utils";
import {
  Check,
  CheckCircle,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  ExternalLink,
  Plus,
  Pencil,
  Trash2,
  XCircle,
  FileText,
  Copy,
  Mail,
  User,
} from "lucide-react";
import { router, useForm, usePage } from "@inertiajs/react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useState, useEffect } from "react";
import { FormationProposedName, PageProps } from "../../../types";
import { FormationProposedNameSheet } from "../../../components/formations/FormationProposedNameSheet";
import { FormationBusinessAddressSheet } from "../../../components/formations/FormationBusinessAddressSheet";
import { FormationContactInfoSheet } from "../../../components/formations/FormationContactInfoSheet";
import { AddPrincipalSheet } from "../../Formations/sheets/AddPrincipalSheet";
import { EditPrincipalSheet } from "../../Formations/sheets/EditPrincipalSheet";
import {
  FormationBusinessAddress,
  FormationContactInfo as FormationContactInfoType,
} from "../../../types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

function SortableProposedNameItem({
  pn,
  onEdit,
  onDelete,
}: {
  pn: any;
  onEdit: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: pn.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing p-1 -ml-1"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
          <span className="text-xs font-medium text-muted-foreground">
            {pn.position + 1}
          </span>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground truncate">
            {pn.name}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={onEdit}
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

type Principal = {
  id: string;
  full_name: string;
  email: string | null;
  principal_kind: string;
  kyc_verified: boolean;
  roles: Record<string, any>;
};

type DocumentAttachment = {
  id: string;
  name: string;
  file_url?: string | null;
  blob_signed_id?: string | null;
  filename?: string | null;
  content_type?: string | null;
  created_at: string;
};

type Formation = {
  id: string;
  legal_name: string;
  assumed_name: string | null;
  stage: string;
  entity_name: string;
  jurisdiction_code: string;
  account_id: string;
  account_name: string;
  account_slug: string;
  payment: {
    id: string;
    service_fee_cents: number;
    state_fee_cents: number;
    total_cents: number;
    paid: boolean;
    paid_at: string | null;
    invoice_number?: string | null;
    receipt_number?: string | null;
  } | null;
  state_filing: {
    id: string;
    jurisdiction_reference: string | null;
    submitted_at: string | null;
    accepted: boolean;
    rejected: boolean;
    pending: boolean;
  } | null;
  completion: {
    id: string;
    completed_at: string;
    notes: string | null;
  } | null;
  principals: Principal[];
  application_documents: DocumentAttachment[];
  filing_documents: DocumentAttachment[];
  completion_documents: DocumentAttachment[];
  proposed_names: { id: string; name: string; position: number }[];
  business_addresses: FormationBusinessAddress[];
  contact_infos: FormationContactInfoType[];
  created_at: string;
  compliance_officer_email: string | null;
  support_officer_email: string | null;
  customer_owner: import('@/types').CustomerOwner | null;
};

type Entity = {
  id: string;
  name: string;
  code: string;
  jurisdiction_code: string;
  state_fee_cents: number;
  service_fee_cents: number;
};

type EntityOption = {
  id: string;
  code: string;
  name: string;
  jurisdiction_code: string;
  country_id: string;
  state_id: string | null;
  state_fee_cents: number;
  service_fee_cents: number;
  total_cents: number;
  processing_days_min: number;
  processing_days_max: number;
};

type Props = {
  formation: Formation;
  entity: Entity;
  entities: EntityOption[];
  supports: Record<string, boolean>;
  countries: { id: string; code: string; name: string }[];
  states: { id: string; code: string; name: string; country_id: string }[];
  local_areas: { id: string; code: string; name: string; state_id: string }[];
};

export default function BackofficeFormationsShow({
  formation,
  entity,
  entities,
  supports,
  countries,
  states,
  local_areas,
}: Props) {
  const { auth } = usePage<PageProps>().props
  const isAdmin = auth.identity?.backoffice_admin ?? false
  const isAssignedOfficer = !isAdmin && auth.identity && (
    auth.identity.email_address === formation.compliance_officer_email ||
    auth.identity.email_address === formation.support_officer_email
  )
  const confirm = useConfirm();
  const [proposedNameSheetOpen, setProposedNameSheetOpen] = useState(false);
  const [editProposedName, setEditProposedName] = useState<
    (typeof formation.proposed_names)[number] | null
  >(null);
  const [proposedNames, setProposedNames] = useState<
    typeof formation.proposed_names
  >(formation?.proposed_names || []);

  const [businessAddressSheetOpen, setBusinessAddressSheetOpen] =
    useState(false);
  const [editBusinessAddress, setEditBusinessAddress] =
    useState<FormationBusinessAddress | null>(null);

  const [contactInfoSheetOpen, setContactInfoSheetOpen] = useState(false);
  const [editContactInfo, setEditContactInfo] =
    useState<FormationContactInfoType | null>(null);

  const [addPrincipalSheetOpen, setAddPrincipalSheetOpen] = useState(false);
  const [editPrincipalSheetOpen, setEditPrincipalSheetOpen] = useState(false);
  const [editPrincipal, setEditPrincipal] = useState<Principal | null>(null);

  // Jurisdiction editor
  const [jurisdictionDialogOpen, setJurisdictionDialogOpen] = useState(false);
  const jurisdictionForm = useForm({ entity_id: entity.id });
  const [jurCountryId, setJurCountryId] = useState(() => {
    const current = entities.find((e) => e.id === entity.id);
    return current?.country_id || "";
  });
  const [jurStateId, setJurStateId] = useState(() => {
    const current = entities.find((e) => e.id === entity.id);
    return current?.state_id || "";
  });
  const jurCountryEntities = entities.filter((e) => e.country_id === jurCountryId);
  const jurEntitiesUseStates = jurCountryEntities.some((e) => e.state_id);
  const jurCountryStates = jurEntitiesUseStates ? states.filter((s) => s.country_id === jurCountryId) : [];
  const jurHasStates = jurCountryStates.length > 0;
  const jurFilteredEntities = jurCountryEntities.filter((e) => {
    if (!jurCountryId) return false;
    if (jurHasStates && !jurStateId) return false;
    if (jurHasStates && jurStateId && e.state_id && e.state_id !== jurStateId) return false;
    return true;
  });

  // State filing dialog
  const [stateFilingDialogOpen, setStateFilingDialogOpen] = useState(false);
  const stateFilingForm = useForm({ jurisdiction_reference: "" });

  // Acceptance dialog
  const [acceptanceDialogOpen, setAcceptanceDialogOpen] = useState(false);
  const acceptanceForm = useForm({ certificate_number: "" });

  // Rejection dialog
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const rejectionForm = useForm({ reason: "", notes: "" });

  // Completion dialog
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const completionForm = useForm({ notes: "" });

  useEffect(() => {
    if (formation?.proposed_names) setProposedNames(formation.proposed_names);
  }, [formation?.proposed_names]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setProposedNames((items: FormationProposedName[]) => {
        const oldIndex = items.findIndex(
          (i: FormationProposedName) => i.id === active.id,
        );
        const newIndex = items.findIndex(
          (i: FormationProposedName) => i.id === over.id,
        );
        const newArray = arrayMove(items, oldIndex, newIndex);

        // Optimistically update positions for UI display
        const updatedArray = newArray.map(
          (item: FormationProposedName, index: number) => ({
            ...item,
            position: index,
          }),
        );

        router.patch(
          `/backoffice/formations/${formation.id}/proposed_names/reorder`,
          {
            ordered_ids: updatedArray.map((i: FormationProposedName) => i.id),
          },
          { preserveScroll: true },
        );

        return updatedArray;
      });
    }
  }

  return (
    <BackofficeLayout
      title={formation.legal_name}
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Formations", href: "/backoffice/formations" },
        { label: formation.legal_name },
      ]}
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {formation.legal_name}
          </h1>
          <StageBadge stage={formation.stage} variant="formation" />
        </div>
        <p className="text-sm text-muted-foreground">
          {entity.name} · {entity.jurisdiction_code}
          {formation.assumed_name && ` · DBA: ${formation.assumed_name}`}
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-1 inline-flex text-muted-foreground hover:text-foreground"
            onClick={() => setJurisdictionDialogOpen(true)}
          >
            <Pencil className="w-3 h-3" />
          </Button>
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Account:{" "}
          <Link
            href={`/${formation.account_slug}/dashboard`}
            className="hover:underline inline-flex items-center gap-1"
          >
            {formation.account_name} <ExternalLink className="w-3 h-3" />
          </Link>
          {" · "}Created {formatDate(formation.created_at)}
        </p>
      </div>

      {/* Customer Owner */}
      {(isAdmin || isAssignedOfficer) && formation.customer_owner ? (
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              Customer Owner
            </h2>
          </div>
          <div className="px-4 py-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Name</span>
              <span className="text-sm font-medium">{formation.customer_owner.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Email</span>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-mono">{formation.customer_owner.email_address}</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => navigator.clipboard.writeText(formation.customer_owner!.email_address)}
                  title="Copy email"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <a
                  href={`mailto:${formation.customer_owner.email_address}`}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Send email"
                >
                  <Mail className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
            {formation.customer_owner.multiple && (
              <p className="text-xs text-muted-foreground pt-1">
                +{formation.customer_owner.all!.length - 1} additional owner{formation.customer_owner.all!.length > 2 ? 's' : ''}
              </p>
            )}
          </div>
        </section>
      ) : (isAdmin || isAssignedOfficer) && formation.customer_owner === null ? (
        <section className="border rounded-lg border-amber-200 bg-amber-50/30">
          <div className="px-4 py-3 text-sm text-amber-700">
            No owner found for this workspace.{' '}
            <Link href={`/backoffice/accounts/${formation.account_id}`} className="underline">
              Investigate
            </Link>
          </div>
        </section>
      ) : null}

      <div className="space-y-6">
        {/* Progress Stepper */}
        <div className="py-4 px-5 border rounded-lg">
          <ProgressStepper
            stages={["application", "filing", "completed"] as const}
            labels={{ application: "Application", filing: "Filing", completed: "Complete" }}
            currentStage={formation.stage}
          />
        </div>

        {/* Lifecycle Actions */}
        {!formation.completion && (
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Next Step</h2>
              <span className="text-xs text-muted-foreground">
                {!formation.payment ? "Payment required" :
                 !formation.payment.paid ? "Awaiting payment" :
                 !formation.state_filing ? "Ready for state filing" :
                 formation.state_filing.pending ? "Awaiting filing decision" :
                 formation.state_filing.accepted ? "Ready to complete" :
                 "Filing rejected"}
              </span>
            </div>
            <div className="px-4 py-3 flex gap-2">
              {/* Create Payment */}
              {!formation.payment && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[13px]"
                  onClick={async () => {
                    if (await confirm({ title: "Create Payment", description: `Create payment of ${formatCents(entity.service_fee_cents + entity.state_fee_cents)} and mark as paid (manual)?`, confirmLabel: "Create Payment" })) {
                      router.post(`/backoffice/formations/${formation.id}/payment`);
                    }
                  }}
                >
                  <CheckCircle className="mr-1.5 h-3 w-3" />
                  Create Payment ({formatCents(entity.service_fee_cents + entity.state_fee_cents)})
                </Button>
              )}

              {/* Mark as Paid (existing unpaid payment) */}
              {formation.payment && !formation.payment.paid && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[13px]"
                  onClick={async () => {
                    if (await confirm({ title: "Mark as Paid", description: `Mark payment of ${formatCents(formation.payment!.total_cents)} as paid (manual)?`, confirmLabel: "Mark as Paid" })) {
                      router.post(`/backoffice/formations/${formation.id}/payments/${formation.payment!.id}/payment_confirmation`);
                    }
                  }}
                >
                  <CheckCircle className="mr-1.5 h-3 w-3" />
                  Mark as Paid ({formatCents(formation.payment.total_cents)})
                </Button>
              )}

              {/* Create State Filing */}
              {formation.payment?.paid && !formation.state_filing && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[13px]"
                  onClick={() => setStateFilingDialogOpen(true)}
                >
                  <FileText className="mr-1.5 h-3 w-3" />
                  Create State Filing
                </Button>
              )}

              {/* Accept / Reject State Filing */}
              {formation.state_filing?.pending && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-[13px]"
                    onClick={() => setAcceptanceDialogOpen(true)}
                  >
                    <CheckCircle className="mr-1.5 h-3 w-3" />
                    Accept Filing
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-[13px] text-destructive hover:text-destructive"
                    onClick={() => setRejectionDialogOpen(true)}
                  >
                    <XCircle className="mr-1.5 h-3 w-3" />
                    Reject Filing
                  </Button>
                </>
              )}

              {/* Complete Formation */}
              {formation.state_filing?.accepted && !formation.completion && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[13px]"
                  onClick={() => setCompletionDialogOpen(true)}
                >
                  <CheckCircle className="mr-1.5 h-3 w-3" />
                  Complete Formation
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Detail Grid */}
        <DetailGrid
          items={[
            { label: "Entity Type", value: entity.name },
            { label: "Jurisdiction", value: entity.jurisdiction_code },
            { label: "Account", value: formation.account_name },
            { label: "Created", value: formatDate(formation.created_at) },
            ...(formation.state_filing ? [
              { label: "Filing Reference", value: formation.state_filing.jurisdiction_reference || "—" },
              { label: "Filed", value: formation.state_filing.submitted_at ? formatDate(formation.state_filing.submitted_at) : "—" },
            ] : []),
            ...(formation.completion ? [
              { label: "Completed", value: formation.completion.completed_at ? formatDate(formation.completion.completed_at) : "—" },
            ] : []),
          ]}
        />

        {/* Payment */}
        <PaymentSummary
          payment={formation.payment ? {
            id: formation.payment.id,
            service_fee_cents: formation.payment.service_fee_cents,
            gov_fee_cents: formation.payment.state_fee_cents,
            total_cents: formation.payment.total_cents,
            paid: formation.payment.paid,
            paid_at: formation.payment.paid_at,
            invoice_number: formation.payment.invoice_number,
            receipt_number: formation.payment.receipt_number,
          } : null}
          adminActions={formation.payment ? {
            invoicePath: `/backoffice/formations/${formation.id}/payments/${formation.payment.id}/invoice`,
          } : undefined}
          collapsible={false}
        />

        {/* State Filing */}
        {formation.state_filing && (
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">
                State Filing
              </h2>
              {formation.state_filing.accepted && (
                <Badge className="bg-green-600 text-white font-normal text-xs">
                  Accepted
                </Badge>
              )}
              {formation.state_filing.rejected && (
                <Badge className="bg-red-600 text-white font-normal text-xs">
                  Rejected
                </Badge>
              )}
              {formation.state_filing.pending && (
                <Badge className="bg-secondary text-secondary-foreground font-normal text-xs">
                  Pending
                </Badge>
              )}
            </div>
            <div className="px-4 py-3 space-y-1.5 text-sm">
              {formation.state_filing.jurisdiction_reference && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-mono text-xs">
                    {formation.state_filing.jurisdiction_reference}
                  </span>
                </div>
              )}
              {formation.state_filing.submitted_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted</span>
                  <span>{formatDate(formation.state_filing.submitted_at)}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Proposed Names */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              Proposed Names
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setEditProposedName(null);
                setProposedNameSheetOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add name
            </Button>
          </div>
          <div className="px-4">
            {proposedNames.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={proposedNames.map(
                    (pn: FormationProposedName) => pn.id,
                  )}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="divide-y">
                    {proposedNames.map((pn: FormationProposedName) => (
                      <SortableProposedNameItem
                        key={pn.id}
                        pn={pn}
                        onEdit={() => {
                          setEditProposedName(pn);
                          setProposedNameSheetOpen(true);
                        }}
                        onDelete={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              description: "Remove this proposed name?",
                              confirmLabel: "Remove",
                              destructive: true,
                            })
                          ) {
                            router.delete(
                              `/backoffice/formations/${formation.id}/proposed_names/${pn.id}`,
                            );
                          }
                        }}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No proposed names added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditProposedName(null);
                    setProposedNameSheetOpen(true);
                  }}
                >
                  Add first choice
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Business Addresses */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              Business Address
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setEditBusinessAddress(null);
                setBusinessAddressSheetOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add address
            </Button>
          </div>
          <div className="px-4">
            {formation.business_addresses?.length > 0 ? (
              <div className="divide-y">
                {formation.business_addresses.map(
                  (addr: FormationBusinessAddress) => (
                    <div
                      key={addr.id}
                      className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                          {addr.label && (
                            <div className="text-xs text-muted-foreground mb-0.5">
                              {addr.label}
                            </div>
                          )}
                          <div className="text-sm font-medium text-foreground">
                            {addr.street_address_1}
                          </div>
                          {addr.street_address_2 && (
                            <div className="text-xs text-muted-foreground">
                              {addr.street_address_2}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {addr.city}
                            {addr.state_province &&
                              `, ${addr.state_province}`}{" "}
                            {addr.postal_code} · {addr.country_code}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            setEditBusinessAddress(addr);
                            setBusinessAddressSheetOpen(true);
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (
                              await confirm({
                                description: "Remove this address?",
                                confirmLabel: "Remove",
                                destructive: true,
                              })
                            )
                              router.delete(
                                `/backoffice/formations/${formation.id}/business_addresses/${addr.id}`,
                              );
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No business address added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditBusinessAddress(null);
                    setBusinessAddressSheetOpen(true);
                  }}
                >
                  Add address
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Contact Info */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              Contact Information
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setEditContactInfo(null);
                setContactInfoSheetOpen(true);
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add contact
            </Button>
          </div>
          <div className="px-4">
            {formation.contact_infos?.length > 0 ? (
              <div className="divide-y">
                {formation.contact_infos.map((ci: FormationContactInfoType) => (
                  <div
                    key={ci.id}
                    className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors"
                  >
                    <div className="min-w-0">
                      {ci.label && (
                        <div className="text-xs text-muted-foreground mb-0.5">
                          {ci.label}
                        </div>
                      )}
                      <div className="text-sm font-medium text-foreground">
                        {ci.email}
                      </div>
                      {ci.phone && (
                        <div className="text-xs text-muted-foreground">
                          {ci.phone}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setEditContactInfo(ci);
                          setContactInfoSheetOpen(true);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              description: "Remove this contact?",
                              confirmLabel: "Remove",
                              destructive: true,
                            })
                          )
                            router.delete(
                              `/backoffice/formations/${formation.id}/contact_infos/${ci.id}`,
                            );
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No contact information added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditContactInfo(null);
                    setContactInfoSheetOpen(true);
                  }}
                >
                  Add contact
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* People */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">People</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {formation.principals.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setAddPrincipalSheetOpen(true)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add person
              </Button>
            </div>
          </div>
          <div className="px-4">
            {formation.principals.length > 0 ? (
              <div className="divide-y">
                {formation.principals.map((p) => (
                  <div
                    key={p.id}
                    className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors cursor-pointer"
                    onClick={() => {
                      setEditPrincipal(p);
                      setEditPrincipalSheetOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <span className="text-xs font-medium text-muted-foreground">
                          {p.full_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {p.full_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.email && <span>{p.email} · </span>}
                          {Object.keys(p.roles || {}).length > 0 ? (
                            Object.keys(p.roles)
                              .map(
                                (r) =>
                                  r.charAt(0).toUpperCase() +
                                  r.slice(1).replace("_", " "),
                              )
                              .join(", ")
                          ) : (
                            <span className="capitalize">
                              {p.principal_kind}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.kyc_verified && (
                        <span className="flex items-center gap-1 text-xs text-green-600 mr-2">
                          <CheckCircle2 className="w-3 h-3" />
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirm({
                              description: "Remove this person?",
                              confirmLabel: "Remove",
                              destructive: true,
                            })
                          ) {
                            router.delete(
                              `/backoffice/formations/${formation.id}/principals/${p.id}`,
                            );
                          }
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <Users className="w-5 h-5 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground mb-2">
                  No people added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddPrincipalSheetOpen(true)}
                >
                  Add person
                </Button>
              </div>
            )}
          </div>
        </section>


        {/* Documents */}
        <DocumentSection
          categories={[
            {
              label: "Application Documents",
              documents: formation.application_documents || [],
              uploadPath: `/backoffice/formations/${formation.id}/application_documents`,
              deletePath: `/backoffice/formations/${formation.id}/application_documents`,
            },
            {
              label: "Filing Documents",
              documents: formation.filing_documents || [],
              uploadPath: `/backoffice/formations/${formation.id}/filing_documents`,
              deletePath: `/backoffice/formations/${formation.id}/filing_documents`,
            },
            {
              label: "Completion Documents",
              documents: formation.completion_documents || [],
              uploadPath: `/backoffice/formations/${formation.id}/completion_documents`,
              deletePath: `/backoffice/formations/${formation.id}/completion_documents`,
            },
          ]}
          templates={(formation as any).templates || []}
        />
      </div>

      <FormationProposedNameSheet
        open={proposedNameSheetOpen}
        onOpenChange={setProposedNameSheetOpen}
        formationId={formation.id}
        proposedName={editProposedName}
        basePath="/backoffice"
      />
      <FormationBusinessAddressSheet
        open={businessAddressSheetOpen}
        onOpenChange={setBusinessAddressSheetOpen}
        formationId={formation.id}
        address={editBusinessAddress}
        basePath="/backoffice"
        countries={countries}
        states={states}
      />
      <FormationContactInfoSheet
        open={contactInfoSheetOpen}
        onOpenChange={setContactInfoSheetOpen}
        formationId={formation.id}
        contact={editContactInfo}
        basePath="/backoffice"
      />

      {/* Principal Sheets */}
      <AddPrincipalSheet
        formationId={formation.id}
        formationName={formation.legal_name}
        supports={supports || {}}
        countries={countries}
        states={states}
        localAreas={local_areas || []}
        open={addPrincipalSheetOpen}
        onOpenChange={setAddPrincipalSheetOpen}
        basePath="/backoffice"
      />
      <EditPrincipalSheet
        formationId={formation.id}
        formationName={formation.legal_name}
        supports={supports || {}}
        principal={editPrincipal}
        countries={countries}
        states={states}
        localAreas={local_areas || []}
        open={editPrincipalSheetOpen}
        onOpenChange={setEditPrincipalSheetOpen}
        basePath="/backoffice"
      />

      {/* Jurisdiction Dialog */}
      <Dialog open={jurisdictionDialogOpen} onOpenChange={setJurisdictionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Jurisdiction</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              jurisdictionForm.patch(`/backoffice/formations/${formation.id}`, {
                onSuccess: () => setJurisdictionDialogOpen(false),
              });
            }}
          >
            <div className="space-y-3 py-4">
              {/* Country */}
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Country</Label>
                <Select
                  value={jurCountryId}
                  onValueChange={(v) => {
                    setJurCountryId(v);
                    setJurStateId("");
                    jurisdictionForm.setData("entity_id", "");
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* State (if applicable) */}
              {jurHasStates && (
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">State</Label>
                  <Select
                    value={jurStateId}
                    onValueChange={(v) => {
                      setJurStateId(v);
                      jurisdictionForm.setData("entity_id", "");
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {jurCountryStates.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Entity Type */}
              {jurFilteredEntities.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Entity Type</Label>
                  <div className="space-y-2">
                    {jurFilteredEntities.map((e) => {
                      const isSelected = jurisdictionForm.data.entity_id === e.id
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => jurisdictionForm.setData("entity_id", e.id)}
                          className={cn(
                            "w-full p-3 text-left rounded-lg border transition-all",
                            isSelected ? "border-foreground bg-muted" : "border-border hover:border-border"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground">{e.name}</span>
                                {isSelected && <Check className="w-4 h-4 text-foreground" />}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {e.processing_days_min}-{e.processing_days_max} days
                                </span>
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-3 h-3" />
                                  {formatCents(e.total_cents)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {jurisdictionForm.errors.entity_id && (
                <p className="text-sm text-destructive">{jurisdictionForm.errors.entity_id}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" onClick={() => setJurisdictionDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={jurisdictionForm.processing || !jurisdictionForm.data.entity_id}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* State Filing Dialog */}
      <Dialog open={stateFilingDialogOpen} onOpenChange={setStateFilingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create State Filing</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              stateFilingForm.post(`/backoffice/formations/${formation.id}/state_filing`, {
                onSuccess: () => {
                  setStateFilingDialogOpen(false);
                  stateFilingForm.reset();
                },
              });
            }}
          >
            <div className="space-y-3 py-4">
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Jurisdiction Reference</Label>
                <Input
                  className="h-9"
                  value={stateFilingForm.data.jurisdiction_reference}
                  onChange={(e) => stateFilingForm.setData("jurisdiction_reference", e.target.value)}
                  placeholder="e.g. REF-123456"
                />
              </div>
              {stateFilingForm.errors.jurisdiction_reference && (
                <p className="text-sm text-destructive">{stateFilingForm.errors.jurisdiction_reference}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" onClick={() => setStateFilingDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={stateFilingForm.processing}>
                Create Filing
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Acceptance Dialog */}
      <Dialog open={acceptanceDialogOpen} onOpenChange={setAcceptanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept State Filing</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              acceptanceForm.post(`/backoffice/formations/${formation.id}/state_filing/acceptance`, {
                onSuccess: () => {
                  setAcceptanceDialogOpen(false);
                  acceptanceForm.reset();
                },
              });
            }}
          >
            <div className="space-y-3 py-4">
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Certificate Number</Label>
                <Input
                  className="h-9"
                  value={acceptanceForm.data.certificate_number}
                  onChange={(e) => acceptanceForm.setData("certificate_number", e.target.value)}
                  placeholder="e.g. CERT-2024-001"
                />
              </div>
              {acceptanceForm.errors.certificate_number && (
                <p className="text-sm text-destructive">{acceptanceForm.errors.certificate_number}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" onClick={() => setAcceptanceDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={acceptanceForm.processing}>
                Accept
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialogOpen} onOpenChange={setRejectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject State Filing</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              rejectionForm.post(`/backoffice/formations/${formation.id}/state_filing/rejection`, {
                onSuccess: () => {
                  setRejectionDialogOpen(false);
                  rejectionForm.reset();
                },
              });
            }}
          >
            <div className="space-y-3 py-4">
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Reason</Label>
                <Input
                  className="h-9"
                  value={rejectionForm.data.reason}
                  onChange={(e) => rejectionForm.setData("reason", e.target.value)}
                  placeholder="Reason for rejection"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Notes (optional)</Label>
                <Textarea
                  value={rejectionForm.data.notes}
                  onChange={(e) => rejectionForm.setData("notes", e.target.value)}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
              {rejectionForm.errors.reason && (
                <p className="text-sm text-destructive">{rejectionForm.errors.reason}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" onClick={() => setRejectionDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={rejectionForm.processing} variant="destructive">
                Reject
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Formation</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              completionForm.post(`/backoffice/formations/${formation.id}/completion`, {
                onSuccess: () => {
                  setCompletionDialogOpen(false);
                  completionForm.reset();
                },
              });
            }}
          >
            <div className="space-y-3 py-4">
              <p className="text-sm text-muted-foreground">
                This will mark the formation as completed and notify the customer.
              </p>
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Notes (optional)</Label>
                <Textarea
                  value={completionForm.data.notes}
                  onChange={(e) => completionForm.setData("notes", e.target.value)}
                  placeholder="Any completion notes..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" onClick={() => setCompletionDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={completionForm.processing}>
                Complete Formation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </BackofficeLayout>
  );
}
